import React, { useEffect, useState } from 'react'
import Modal from 'components/Modal'
import { Text } from 'components/Text'
import {
  ItemsContainer,
  TransactionAmountItem,
  TransactionItem,
  TransactionModalContainer,
  TransactionModalContentContainer,
  TransactionModalItem,
  TransactionModalItemContainer,
  TransactionPaymentContainer,
  TransactionPaymentItem,
} from './TransactionModal.styled'
import { TransactionModalProps } from '.'
import Divider from 'components/Divider'
import Select from 'components/Select'
import { Input } from 'components/Input'
import CheckBox from 'components/CheckBox/CheckBox'
import Button from '../Button/Button'
import RadioButton from '../RadioButton/RadioButton'
import { useAppDispatch, useAppState } from '../../store'
import { transactionActions } from '../../store/slices/transactions/transactions.slice'
import { SelectTypes } from 'components/Select/Select.props'
import { find, omit, parseInt } from 'lodash'
import { ProItems, ProShopCategoryItem } from 'models/location'
import { clientActions } from 'store/slices/clients/clients.slice'
import { TransactionPayment } from '../../models/transaction.model'
import { CreateTransactionRequest } from 'services/transaction'
import Spinner from 'components/Spinner'
import cogoToast from 'cogo-toast'
import { PENNIES } from 'utils/rate'
import { useAmplitude } from 'hooks/useAmplitude'
import { AMPLITUDE_EVENTS } from 'amplitude/events'

type PaymentMethods =
  | 'none'
  | 'cash'
  | 'credit_card'
  | 'card_on_file'
  | 'account_balance'

const TransactionModal = ({ onClose, locationId }: TransactionModalProps) => {
  const dispatch = useAppDispatch()
  const {
    transactionType,
    categories,
    categoriesDropdown,
    proShopItems,
    totalAmount,
    taxAmount: tax,
    subTotalAmount,
    createLoading,
    loading,
  } = useAppState(state => state.transaction)
  const { location } = useAppState(state => state.appointments)
  const { players } = useAppState(state => state.clients)
  const [itemsRow, setItemsRow] = useState<number>(1)
  const [payment, setPayment] = useState<PaymentMethods>('none')
  // @ts-ignore
  const itemsToRender = [...Array(itemsRow).keys()]
  const [selectedType, setSelectedType] = useState<string>('')
  const [itemsList, setItemList] = useState<SelectTypes[]>([])
  const [selectedItems, setSelectedItems] = useState<Record<number, ProItems>>()
  const [currentCategoriesItem, serCurrentCategoriesItem] = useState<
    ProShopCategoryItem[]
  >([])
  const [playersName, setPlayersName] = useState<SelectTypes[]>([])
  const [taxAmount, setTaxAmount] = useState<number>(0)
  const [playersEmail, setPlayersEmail] = useState<SelectTypes[]>([])
  const [selectedPlayerName, setSelectedPlayerName] = useState<
    SelectTypes | undefined
  >(undefined)
  const [selectedPlayerEmail, setSelectedPlayerEmail] = useState<
    SelectTypes | undefined
  >(undefined)
  const [currentCategory, setCurrentCategory] = useState<
    SelectTypes | undefined
  >()
  const [saveTransaction, setSaveTransaction] = useState<boolean>(false)
  const { logEvent } = useAmplitude()

  useEffect(() => {
    dispatch(transactionActions.getProShop(locationId))
    logEvent(AMPLITUDE_EVENTS.APPOINTMENT_MODAL_EVENT, {
      location: locationId,
      modal: 'create appointment',
    })
  }, [])

  useEffect(() => {
    if (location && location?.tax) {
      setTaxAmount(location.tax)
    }
  }, [location])

  useEffect(() => {
    if (selectedType === 'Pro Shop Sale') {
      const items = proShopItems.map(pro => {
        return { label: pro.item, value: pro.id.toString() }
      })
      setItemList(items.length > 0 ? items : [])
      setCurrentCategory(undefined)
    } else {
      setItemList([])
      setCurrentCategory(undefined)
    }
    setItemsRow(1)
    setSelectedItems({})
  }, [proShopItems, selectedType])

  useEffect(() => {
    if (players && players.length > 0) {
      const newPlayersName = players.map(player => {
        return { label: player.title, value: player.id.toString() }
      })
      const newPlayersEmail = players.map(player => {
        return { label: player.email, value: player.id.toString() }
      })
      setPlayersName(newPlayersName)
      setPlayersEmail(newPlayersEmail)
    }
  }, [players])

  useEffect(() => {
    calculateAmount()
  }, [selectedItems, taxAmount])

  const handlePlayers = (text: string) => {
    if (text.length >= 3) {
      dispatch(
        clientActions.getClientPlayers({
          locationId: locationId ? locationId.toString() : '',
          page: 0,
          limit: PENNIES,
          search_text: text,
        })
      )
      return
    }
  }

  const handleDropdown = (
    element: SelectTypes | null,
    type: 'type' | 'category'
  ) => {
    if (type === 'type') {
      if (element) {
        setSelectedType(element.value)
      } else {
        setSelectedType('')
      }
      return
    }
    if (type === 'category') {
      if (element) {
        setCurrentCategory(element)
        const category = find(categories, {
          program_id: parseInt(element.value),
        })
        if (category && category.items) {
          const categories = [...category.items]
          const list: SelectTypes[] = []
          const newItems = []
          categories.forEach(elem => {
            list.push({
              label: elem.item,
              value: elem.appointment_id
                ? elem.appointment_id.toString()
                : elem.item,
            })
            const newElem = Object.assign({ quantity: 0 }, elem)
            newItems.push(newElem)
          })
          serCurrentCategoriesItem(category ? category.items : [])
          setItemList(list as SelectTypes[])
        }
      } else {
        setCurrentCategory(undefined)
      }
      return
    }
  }

  const handleSelectedPlayer = (
    selected: SelectTypes,
    type: 'name' | 'email'
  ) => {
    if (selected) {
      if (type === 'name') {
        setSelectedPlayerName(selected)
        const email = handleDefaultPlayer(selected, 'email')
        email && setSelectedPlayerEmail(email)
        return
      }
      if (type === 'email') {
        setSelectedPlayerEmail(selected)
        const player = handleDefaultPlayer(selected, 'name')
        player && setSelectedPlayerName(player)
        return
      }
    } else {
      setSelectedPlayerName(undefined)
      setSelectedPlayerEmail(undefined)
    }
  }

  const handleDefaultPlayer = (
    selected: SelectTypes,
    type: 'name' | 'email'
  ) => {
    if (players && selected) {
      const client = find(players, { id: parseInt(selected.value) })
      if (client) {
        return {
          label: type === 'name' ? client.name : client.email,
          value: client?.id.toString(),
        }
      }
    }
  }

  const handleItem = (
    item: SelectTypes,
    index: number,
    type: 'type' | 'category'
  ) => {
    if (item) {
      if (type === 'type') {
        const proItem = find(proShopItems, { id: parseInt(item.value) })
        if (proItem) {
          setSelectedItems(items => ({ ...items, [index]: proItem }))
        }
        return
      }
      if (type === 'category') {
        const newItems = find(currentCategoriesItem, {
          appointment_id: parseInt(item.value),
        })
        if (newItems) {
          setSelectedItems(items => ({ ...items, [index]: newItems }))
        }
        return
      }
    } else {
      const items = { ...selectedItems }
      setSelectedItems(omit(items, [index]))
    }
  }

  const handleQuantity = (qty: number, itemIndex: number) => {
    if (qty) {
      const currentItem = selectedItems && { ...selectedItems[itemIndex] }
      if (currentItem) {
        currentItem.quantity = qty
        setSelectedItems(items => ({ ...items, [itemIndex]: currentItem }))
      }
    }
  }

  const handlePrice = (price: string, itemIndex: number) => {
    if (price) {
      const currentItem = selectedItems && { ...selectedItems[itemIndex] }
      const pricePrefix = price.replace('$', '')
      const newPrice = pricePrefix.replaceAll(',', '')
      if (currentItem) {
        currentItem.price = parseFloat(newPrice)
        setSelectedItems(items => ({ ...items, [itemIndex]: currentItem }))
      }
    }
  }

  const calculateAmount = () => {
    if (selectedItems) {
      let subtotal = 0
      for (const [, value] of Object.entries(selectedItems)) {
        const { price, quantity } = value
        if (price && quantity) {
          subtotal = subtotal + price * quantity
        }
      }
      if (subtotal > 0 && taxAmount >= 0) {
        const newSubtotal = subtotal.toFixed(2)
        dispatch(
          transactionActions.getTotalTransactionAmount({
            locationId,
            tax_rate: taxAmount / PENNIES,
            subtotal: parseFloat(newSubtotal) * PENNIES,
          })
        )
      }
    }
  }

  const createPaymentBody = () => {
    const payments: TransactionPayment[] = []
    if (selectedItems && selectedPlayerName && selectedPlayerEmail) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      for (const _item in selectedItems) {
        payments.push({
          invoice_required: true,
          user_id: selectedPlayerName.value,
          user_email: selectedPlayerEmail.label,
          payment_required: true,
          amount: totalAmount,
          subtotal: subTotalAmount,
          tax,
          credit_card_processing_fee: null,
          discount: 0,
          description: `Sale: ${selectedType}`,
          payment_method: {
            payment_required: true,
            payment_method_id: null,
            payment_type: payment,
            card_processor: '',
          },
        })
      }
    }

    return payments
  }

  const handleSubmit = async () => {
    const payments = createPaymentBody()
    if (selectedPlayerName && selectedPlayerEmail) {
      const body: CreateTransactionRequest = {
        locationId: locationId.toString(),
        body: {
          payments,
          transaction_item: {
            transaction_type: 'pro_shop_sale',
          },
          users: [
            {
              id: selectedPlayerName.value,
              title: selectedPlayerName.label,
              email: selectedPlayerEmail.label,
            },
          ],
        },
      }
      await dispatch(transactionActions.createTransaction(body)).then(
        (data: any) => {
          if (!data.paylaod.error) {
            cogoToast.success('Transaction created successfully', {
              position: 'top-right',
            })
            onClose()
          }
        }
      )
    }
  }
  
  return (
    <Modal
      showClose
      onClose={() => {
        onClose()
        dispatch(transactionActions.cleanAmounts())
      }}
      title="Create Transaction"
      containerStyle={{ minHeight: 750 }}>
      {createLoading === 'loading' || loading === 'loading' ? (
        <TransactionModalContainer>
          <Spinner />
        </TransactionModalContainer>
      ) : (
        <>
          <TransactionModalContentContainer>
            <TransactionModalItemContainer>
              <TransactionModalItem>
                <Select
                  options={transactionType}
                  label="Type"
                  autocomplete
                  value={
                    selectedType
                      ? { label: selectedType, value: selectedType }
                      : undefined
                  }
                  onChange={types =>
                    handleDropdown(types as SelectTypes, 'type')
                  }
                />
              </TransactionModalItem>
              <TransactionModalItem>
                <Select
                  options={playersName}
                  label="Customer name"
                  autocomplete
                  onInputChange={handlePlayers}
                  value={selectedPlayerName}
                  onChange={player =>
                    handleSelectedPlayer(player as SelectTypes, 'name')
                  }
                />
              </TransactionModalItem>
            </TransactionModalItemContainer>
            <TransactionModalItemContainer>
              <TransactionModalItem>
                <Select
                  options={categoriesDropdown}
                  label="Category"
                  autocomplete
                  disabled={selectedType === 'Pro Shop Sale' ? true : false}
                  value={currentCategory}
                  onChange={types =>
                    handleDropdown(types as SelectTypes, 'category')
                  }
                />
              </TransactionModalItem>
              <TransactionModalItem>
                <Select
                  options={playersEmail}
                  value={selectedPlayerEmail}
                  label="Email"
                  autocomplete
                  onInputChange={handlePlayers}
                  onChange={player =>
                    handleSelectedPlayer(player as SelectTypes, 'email')
                  }
                />
              </TransactionModalItem>
            </TransactionModalItemContainer>
            {itemsToRender.map(element => {
              const selected = selectedItems && selectedItems[element]
              const quantity = selected
                ? selected.quantity
                  ? selected.quantity
                  : 1
                : 1
              return (
                <TransactionModalItemContainer key={element}>
                  <TransactionModalItem>
                    <Select
                      options={itemsList}
                      label="Item"
                      autocomplete
                      value={
                        selected
                          ? {
                              label: selected.item,
                              value: selected.id || selected.appointment_id,
                            }
                          : undefined
                      }
                      onChange={item =>
                        handleItem(
                          item as SelectTypes,
                          element,
                          selectedType === 'Pro Shop Sale' ? 'type' : 'category'
                        )
                      }
                    />
                  </TransactionModalItem>
                  <TransactionModalItem>
                    <ItemsContainer>
                      <TransactionItem>
                        <Input
                          label="Quantity"
                          value={quantity}
                          mask={' '}
                          format={'####'}
                          onChange={qty =>
                            handleQuantity(parseInt(qty.target.value), element)
                          }
                        />
                      </TransactionItem>
                      <TransactionItem>
                        <Input
                          currencyMask
                          value={
                            selected
                              ? (selected.price * quantity).toFixed(2)
                              : 0
                          }
                          label="Price"
                          onChange={price => {
                            handlePrice(price.target.value, element)
                          }}
                        />
                      </TransactionItem>
                    </ItemsContainer>
                  </TransactionModalItem>
                </TransactionModalItemContainer>
              )
            })}
            <Button
              text="More"
              preset="filled"
              variant="primary"
              size="small"
              onClick={() => setItemsRow(itemsRow + 1)}
              style={{ width: '20%', margin: 10 }}
            />

            <TransactionModalItemContainer>
              <div style={{ marginLeft: 10 }}>
                <CheckBox
                  weight="500"
                  text="Save"
                  onChange={() => setSaveTransaction(!saveTransaction)}
                />
              </div>
            </TransactionModalItemContainer>
          </TransactionModalContentContainer>
          <Divider style={{ margin: '30px 0' }} />
          <TransactionPaymentContainer>
            <TransactionModalContentContainer>
              <Text text="Total" weight="600" style={{ marginBottom: 10 }} />
              <TransactionAmountItem>
                <Input
                  label="Subtotal"
                  currencyMask
                  value={subTotalAmount > 0 ? subTotalAmount / PENNIES : 0}
                  disabled
                />
              </TransactionAmountItem>
              <TransactionAmountItem>
                <Input
                  value={taxAmount}
                  type={'number'}
                  label="Tax"
                  onTextChange={text => {
                    const value = parseFloat(text)
                    if (value) {
                      setTaxAmount(value)
                    } else {
                      setTaxAmount(0)
                    }
                  }}
                />
              </TransactionAmountItem>
              <TransactionAmountItem>
                <Input
                  currencyMask
                  label="Total"
                  value={
                    totalAmount > 0 ? (totalAmount / PENNIES).toFixed(2) : 0
                  }
                  disabled
                />
              </TransactionAmountItem>
              <Button
                text="Submit"
                preset="filled"
                variant="primary"
                size="small"
                onClick={handleSubmit}
                style={{ width: '40%', margin: '20px 0' }}
              />
            </TransactionModalContentContainer>
            <TransactionModalContentContainer>
              <Text
                text="Payment Method"
                weight="600"
                style={{ marginBottom: 10 }}
              />
              <TransactionPaymentItem>
                <RadioButton
                  key="radio-payment-1"
                  weight="500"
                  name="payment"
                  text={['None']}
                  value="None"
                  onChange={value =>
                    value && setPayment(value as PaymentMethods)
                  }
                  checked={payment === 'none'}
                />
              </TransactionPaymentItem>
              <TransactionPaymentItem>
                <RadioButton
                  key="radio-payment-2"
                  weight="500"
                  name="payment"
                  text={['Cash']}
                  value="cash"
                  onChange={value =>
                    value && setPayment(value as PaymentMethods)
                  }
                />
              </TransactionPaymentItem>
              <TransactionPaymentItem>
                <RadioButton
                  key="radio-payment-3"
                  weight="500"
                  name="payment"
                  text={['Account Balance']}
                  value="account_balance"
                  onChange={value => value && setPayment('account_balance')}
                />
              </TransactionPaymentItem>

              <TransactionPaymentItem>
                <RadioButton
                  key="radio-payment-4"
                  weight="500"
                  name="payment"
                  text={['Credit Card']}
                  value="credit_card"
                  onChange={value =>
                    value && setPayment(value as PaymentMethods)
                  }
                />
              </TransactionPaymentItem>
            </TransactionModalContentContainer>
          </TransactionPaymentContainer>
        </>
      )}
    </Modal>
  )
}

export default TransactionModal
